'use client';

import React from "react";
import "./terms.modules.css";
import Footer from "@components/footer/Footer";
import Navbar from "@components/Navbar";

const TermsOfService = () => {
  return (
    <div>
      <Navbar />

      {/* Header Section */}
      <header className="tos-header">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated December 17, 2025</p>
      </header>

      {/* Main Content */}
      <main className="tos-content">
        <h2>Welcome to PenguinLearn!</h2>
        <p>
          <strong>PenguinLearn Holdings LLC, doing business as PenguinLearn</strong> (“PenguinLearn,” “we,” “us,”
          “our”) provides its services to you through our website located at <strong>penguinlearn.com</strong> (the
          “Site”) and related services (collectively, the Site and services, the “Services”), subject to the
          following Terms of Service (as amended from time to time, the “Terms”).
        </p>

        <p>
          We may update these Terms from time to time. If we make material changes, we will post the updated Terms
          on this page and update the “Last updated” date. Changes become effective no earlier than fourteen (14)
          days after posting, except for changes addressing new features or legal requirements, which may be
          effective immediately. By continuing to use the Services after changes become effective, you agree to
          the revised Terms.
        </p>

        <p>
          <strong>Please be advised:</strong> These Terms include an arbitration agreement and class action waiver
          that affect your rights (see the “Dispute Resolution by Binding Arbitration” section below). In general,
          this means that, with limited exceptions, you agree to resolve disputes through individual arbitration
          rather than court, and you waive the right to a jury trial and to participate in class or representative
          actions.
        </p>

        <p>
          When using certain aspects of the Services, you may be subject to additional terms and policies posted
          on the Site, including our Privacy Policy, Community Standards, class policies, and Help Center guidance.
          All such terms are incorporated by reference into these Terms.
        </p>

        <p>
          The Services include an online marketplace and platform through which Teachers may offer Classes for sale
          to Parents/Guardians, and Parents/Guardians may purchase Classes for the benefit of their children.
          You acknowledge and agree that: (1) PenguinLearn is not a party to agreements entered into between
          Teachers and Parents/Guardians, (2) PenguinLearn provides tools to facilitate the purchase, sale, and
          delivery of Classes, (3) Parents/Guardians contract for Classes directly with Teachers, (4) PenguinLearn
          is not a broker, agent (except as expressly stated below), or insurer, and (5) PenguinLearn disclaims
          liability for the conduct of Teachers, Parents/Guardians, or other users.
        </p>

        <h2>Key Terms</h2>
        <p>
          <strong>“User,” “you,” or “your”</strong> means a person or entity using the Services, including
          Parents/Guardians and Teachers.
        </p>
        <p>
          <strong>“Parent(s)/Guardian(s)”</strong> means a parent or legal guardian who registers an account to
          enroll a child in Classes.
        </p>
        <p>
          <strong>“Learner(s)”</strong> means a child participating in Classes or learning activities through a
          Parent/Guardian account.
        </p>
        <p>
          <strong>“Teacher(s)”</strong> means an independent instructor (individual) who registers to offer Classes and has an agreement with PenguinLearn to offer Classes.
        </p>
        <p>
          <strong>“Class(es)”</strong> means any online class submitted by a Teacher and offered through the Services.
        </p>

        <h2>Access and Use of the Services</h2>
        <h3>Services Description</h3>
        <p>
          PenguinLearn is an online marketplace for K–12 learning experiences. As the provider of an online
          marketplace, PenguinLearn does not create, control, or manage Classes. PenguinLearn’s responsibilities
          are limited to: (i) providing the platform to facilitate the sale, purchase, and delivery of Classes,
          and (ii) serving as the limited payments agent of Teachers for purposes of accepting payments from
          Parents/Guardians on behalf of Teachers.
        </p>

        <p>
          There are risks when interacting with other Users (including those who may act under false pretenses).
          While we work to promote a safe environment, you agree that these risks are ultimately borne by you,
          and not PenguinLearn. PenguinLearn does not control the quality or outcomes of Classes and cannot
          guarantee authenticity, quality, safety, legality, or appropriateness of Classes.
        </p>

        <h3>Your Registration Obligations</h3>
        <p>
          You may need to register to use certain features of the Services. You agree to provide accurate,
          current, and complete information and to keep it updated. Information you provide is governed by our
          Privacy Policy.
        </p>

        <p>
          You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account.
          If you are a Parent/Guardian, you are responsible for your child’s access to and use of the Services and
          agree to these Terms on your child’s behalf.
        </p>

        <h3>Account, Password, and Security</h3>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for all
          activities occurring under your account. You agree to notify PenguinLearn immediately of any
          unauthorized use of your account or security breach and to sign out after each session.
        </p>

        <h3>Modifications to Services</h3>
        <p>
          PenguinLearn may modify, suspend, or discontinue the Services (or any part) at any time, with or without
          notice. You agree that PenguinLearn will not be liable for any such modification, suspension, or
          discontinuance.
        </p>

        <h3>General Practices</h3>
        <p>
          PenguinLearn may establish general practices and limits regarding use of the Services, including data
          retention and storage limits. PenguinLearn is not responsible for deletion or failure to store data or
          content. We may terminate accounts inactive for an extended period.
        </p>

        <h3>Class Recordings</h3>
        <p>
          Classes conducted through our live class experience may be recorded (“Class Recordings”). Our use of
          Class Recordings is limited to safety, quality assurance, dispute resolution, support, and compliance.
          Recordings may be made available to the Teacher and may be shared with enrolled families for educational
          review.
        </p>

        <p>
          PenguinLearn may review recordings for platform safety, customer support, compliance, and to improve the
          Services. We will not use Class Recordings containing images of you or your child for promotional
          purposes without your express consent.
        </p>

        <h2>Certain Teacher Obligations</h2>
        <h3>Marketplace Basics</h3>
        <p>
          Teachers must provide accurate class listing information, including descriptions, pricing, and schedules.
          PenguinLearn has sole discretion over which Classes may be listed and may reject or remove any Class or
          suspend/remove any Teacher for any reason, including for safety or policy compliance. PenguinLearn may
          edit Class listing details to conform to marketplace standards.
        </p>

        <h3>Background Checks</h3>
        <p>
          Teachers are required to complete a <strong>background check</strong> as part of onboarding and/or ongoing
          safety requirements. Background checks may be conducted by third-party screening providers. You agree to
          provide truthful information and any required consents for screening.
        </p>

        <h3>Community Standards</h3>
        <p>
          You agree to follow our community and safety standards, including professional communications with
          learners, parents/guardians, and other members of the community. Prohibited conduct includes bullying,
          harassment, hate speech, threats, sharing private or personal information of learners or families, and
          any behavior that PenguinLearn determines undermines a safe learning environment.
        </p>

        <h3>Learner Safety & Mandatory Reporting</h3>
        <p>
          Teachers must report suspected child abuse or neglect consistent with applicable laws and any platform
          requirements. PenguinLearn may also report suspected abuse or neglect where appropriate.
        </p>

        <h3>Teacher Responsibilities</h3>
        <p>
          Teachers are responsible for obtaining any licenses or permissions required to offer their Classes and
          for complying with applicable laws. Teachers are independent contractors and not employees of PenguinLearn.
        </p>

        <p>
          PenguinLearn is authorized to act as a Teacher’s limited agent solely for the purpose of accepting
          payments from Parents/Guardians on the Teacher’s behalf and transmitting payments to the Teacher
          (minus fees). Teachers may not share Class Recordings with third parties except as permitted for the
          enrolled class and consistent with PenguinLearn policies.
        </p>

        <h2>Payment Terms</h2>
        <h3>General</h3>
        <p>
          Parents/Guardians agree to pay all applicable fees for Classes (“Enrollment Fees”) as displayed at the
          time of purchase. Fees are charged in the currency shown at checkout. You are responsible for applicable
          taxes except taxes based on PenguinLearn’s net income.
        </p>

        <h3>Payments Processor</h3>
        <p>
          PenguinLearn uses <strong>Stripe</strong> to process payments. Stripe processes payment information in
          accordance with Stripe’s policies. PenguinLearn may receive limited payment-related information such as
          confirmation of payment, transaction identifiers, and purchase status.
        </p>

        <h3>Teacher Payouts</h3>
        <p>
          PenguinLearn will remit Enrollment Fees to Teachers according to the payout schedule and policies
          , minus PenguinLearn’s platform fees. PenguinLearn may delay or withhold
          payouts where necessary to address refunds, disputes, fraud prevention, or policy violations.
        </p>

        <h3>Limited Payment Collections Agent</h3>
        <p>
          Each Teacher appoints PenguinLearn as the Teacher’s limited payments collection agent solely for accepting
          Enrollment Fees from Parents/Guardians. Payment to PenguinLearn is treated as payment to the Teacher.
        </p>

        <h2>Conditions of Use</h2>
        <h3>User Conduct</h3>
        <p>
          You are responsible for any content you upload, post, send, or otherwise make available through the
          Services (“Content”). You agree not to use the Services to:
        </p>
        <ul>
          <li>violate any law or regulation;</li>
          <li>harass, bully, threaten, or harm others;</li>
          <li>post or share content that is obscene, pornographic, hateful, or violent;</li>
          <li>share personal information of learners or families in violation of our policies;</li>
          <li>impersonate another person or entity;</li>
          <li>send spam or unauthorized promotional messages;</li>
          <li>introduce malware or attempt to disrupt the Services;</li>
          <li>attempt to access the Services or systems through unauthorized means.</li>
        </ul>

        <h3>Commercial Use</h3>
        <p>
          Unless expressly authorized, you agree not to copy, distribute, license, sell, resell, or exploit any
          portion of the Services for commercial purposes.
        </p>

        <h2>Intellectual Property Rights</h2>
        <h3>Service Content, Software, and Trademarks</h3>
        <p>
          The Services may include content protected by intellectual property laws (“Service Content”). Except as
          expressly authorized, you agree not to copy, modify, scrape, reverse engineer, or create derivative works
          from the Services or Service Content. The technology and software underlying the Services are owned by
          PenguinLearn and/or its partners.
        </p>

        <p>
          The PenguinLearn name and logos are trademarks of PenguinLearn. Other names and logos may be trademarks of
          their respective owners.
        </p>

        <h3>User Content</h3>
        <p>
          You retain ownership of Content you submit (“User Content”), but you grant PenguinLearn a license to use
          it as necessary to operate, provide, and improve the Services. Public User Content (if any) may be used
          as described on the Services. Private User Content is used only to provide the Services.
        </p>

        <h3>Copyright Complaints (DMCA)</h3>
        <p>
          If you believe content on PenguinLearn infringes your copyright, email{" "}
          <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a> with the subject line{" "}
          <strong>“DMCA Takedown Request”</strong> and include the details required by law (identification of the
          copyrighted work, the infringing content/link, your contact info, and your statements under penalty of
          perjury).
        </p>

        <h2>Third-Party Websites and Tools</h2>
        <p>
          The Services may link to or integrate with third-party websites or tools. PenguinLearn does not control
          those third parties and is not responsible for their content, policies, or practices. Your use of third
          parties is at your own risk.
        </p>

        <h2>Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless PenguinLearn and its affiliates, officers, employees, directors,
          and agents from claims arising out of your use of the Services, your User Content, or your violation of
          these Terms or another party’s rights.
        </p>

        <h2>Disclaimer of Warranties</h2>
        <p>
          YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK. THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.”
          PENGUINLEARN DISCLAIMS ALL WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
        </p>

        <h2>Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, PENGUINLEARN WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, GOODWILL, DATA, OR OTHER INTANGIBLE
          LOSSES, ARISING FROM OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICES.
        </p>

        <p>
          IN NO EVENT WILL PENGUINLEARN’S TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID TO
          PENGUINLEARN IN THE LAST SIX (6) MONTHS, OR, IF GREATER, ONE HUNDRED DOLLARS ($100).
        </p>

        <p>
          SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, SO SOME OF THE ABOVE MAY NOT APPLY TO YOU.
        </p>

        <h2>Dispute Resolution by Binding Arbitration</h2>
        <p>
          <strong>PLEASE READ THIS SECTION CAREFULLY.</strong> It affects your legal rights.
        </p>

        <h3>Agreement to Arbitrate</h3>
        <p>
          You and PenguinLearn agree that disputes arising out of or relating to these Terms or the Services will
          be resolved through final and binding arbitration on an individual basis, except that you may bring
          individual claims in small claims court if eligible.
        </p>

        <h3>Class Action Waiver</h3>
        <p>
          YOU AND PENGUINLEARN AGREE THAT EACH MAY BRING CLAIMS ONLY ON AN INDIVIDUAL BASIS AND NOT AS A PLAINTIFF OR
          CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION.
        </p>

        <h3>Pre-Arbitration Dispute Resolution</h3>
        <p>
          Most issues can be resolved by contacting support at{" "}
          <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>. If not resolved, the party seeking
          arbitration must first send written notice describing the dispute and requested relief. If not resolved
          within sixty (60) days, either party may begin arbitration.
        </p>

        <h3>Arbitration Procedures</h3>
        <p>
          Arbitration will be administered by the American Arbitration Association (“AAA”) under its applicable
          consumer rules, as modified by these Terms. AAA information is available at{" "}
          <a href="https://www.adr.org/" target="_blank" rel="noreferrer">
            https://www.adr.org/
          </a>
          .
        </p>

        <h3>Opt Out</h3>
        <p>
          If you are a new user, you may opt out of arbitration within 30 days after first accepting these Terms by
          emailing <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a> with your name, account
          email, and a clear request to opt out of arbitration.
        </p>

        <h2>Termination</h2>
        <p>
          PenguinLearn may suspend or terminate your account or access to the Services at any time for any reason,
          including violations of these Terms or policies. Termination may result in loss of access to content and
          records associated with your account.
        </p>

        <h2>User Disputes</h2>
        <p>
          You are solely responsible for your interactions with other Users. PenguinLearn may (but is not required
          to) assist with disputes between Users.
        </p>
        <h2>General</h2>
        <p>
          These Terms constitute the entire agreement between you and PenguinLearn regarding the Services.
          If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
          shall remain in full force and effect.
        </p>
                  
        <p>
          <strong>Governing Law.</strong> These Terms are governed by the laws of the State of Delaware,
          without regard to conflict of law principles, except where prohibited by applicable law.
        </p>
                  
        <p>
          <strong>Venue for Non-Arbitrable Claims.</strong> Except where prohibited by applicable law,
          any dispute or claim not subject to arbitration shall be brought exclusively in the state or
          federal courts located in the State of Delaware, and you and PenguinLearn consent to the
          personal jurisdiction of such courts.
        </p>
                  
        <h3>Your Privacy</h3>
        <p>
          We respect the privacy of our users. For details, please see our{" "}
          <a href="/footer/privacy-policy">Privacy Policy</a>. By using the Services, you consent to the
          collection and use of personal information as described there.
        </p>
                  
        <h3>Notice for California Users</h3>
        <p>
          Under California Civil Code Section 1789.3, California users are entitled to specific consumer
          rights information. The Complaint Assistance Unit of the Division of Consumer Services of the
          California Department of Consumer Affairs may be contacted in writing at 1625 North Market Blvd.,
          Suite N 112, Sacramento, CA 95834, or by telephone at (916) 445-1254 or (800) 952-5210.
        </p>
                  
        <h3>Questions?</h3>
        <p>
          Contact us at{" "}
          <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
        </p>
        
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;